import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TeamMember {
    name: string;
    slug: string;
    position: string;
    department: string;
    main_image: string;
}

interface CompanyProps {
    teamMembers: TeamMember[];
}

/* ------------------------------------------------------------------ */
/*  Animation Tokens                                                   */
/* ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1] as const;

const containerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: EASE },
    },
};

/* ------------------------------------------------------------------ */
/*  Theme Colors — variant-aware class map                             */
/* ------------------------------------------------------------------ */

function sectionColors(variant: 'teal' | 'ivory') {
    const isDark = variant === 'teal';
    return {
        bg: isDark ? 'bg-[#0e2727]' : 'bg-[#fdfaf3]',
        heading: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
        kicker: isDark ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60',
        body: isDark ? 'text-[#F5F5F5]/75' : 'text-[#0e2727]/75',
        bodyStrong: isDark ? 'text-[#F5F5F5]/80' : 'text-[#0e2727]/80',
        rule: isDark ? 'bg-[#F5F5F5]/20' : 'bg-[#0e2727]/20',
        border: isDark ? 'border-[#F5F5F5]/10' : 'border-[#0e2727]/10',
        borderHover: isDark ? 'hover:border-[#F5F5F5]/25' : 'hover:border-[#0e2727]/25',
        muted: isDark ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/70',
        accent: isDark ? 'text-[#F5F5F5]/40' : 'text-[#0e2727]/40',
    };
}

/* ------------------------------------------------------------------ */
/*  Section Nav — sticky year-marker navigation                        */
/* ------------------------------------------------------------------ */

const NAV_ITEMS = [
    { id: 'origins', label: 'Origins' },
    { id: 'discovery', label: 'Discovery' },
    { id: '2021', label: '2021' },
    { id: '2022', label: '2022' },
    { id: '2023', label: '2023' },
    { id: '2024', label: '2024' },
    { id: '2025', label: '2025' },
    { id: '2026', label: '2026' },
    { id: 'team', label: 'Team' },
];

function SectionNav({ shouldReduceMotion }: { shouldReduceMotion: boolean | null }) {
    const [activeId, setActiveId] = useState(NAV_ITEMS[0].id);
    const [isPinned, setIsPinned] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    /* Detect when nav becomes sticky */
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 0 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    /* Track which section is in view */
    useEffect(() => {
        const sections = NAV_ITEMS.map((item) => document.getElementById(item.id)).filter(Boolean) as HTMLElement[];
        if (!sections.length) return;
        const obs = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                }
            },
            { rootMargin: '-130px 0px -60% 0px' },
        );
        sections.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, []);

    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <>
            <div ref={sentinelRef} className="h-0" />
            <nav
                aria-label="Company timeline sections"
                className={`sticky top-14 z-30 border-b transition-all duration-300 md:top-16 ${
                    isPinned ? 'border-[#F5F5F5]/10 bg-[#0e2727]/95 backdrop-blur-md' : 'border-transparent bg-[#0e2727]'
                }`}
            >
                <div className="nav-px scrollbar-none mx-auto flex items-center gap-1 overflow-x-auto py-3 sm:justify-center sm:gap-2">
                    {NAV_ITEMS.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            aria-current={activeId === item.id ? 'true' : undefined}
                            className={`lux-caps lux-caps-sm relative shrink-0 px-3 py-1.5 transition-colors ${
                                activeId === item.id ? 'text-[#F5F5F5]' : 'text-[#F5F5F5]/60 hover:text-[#F5F5F5]/80'
                            }`}
                        >
                            {item.label}
                            {activeId === item.id && (
                                <motion.span
                                    layoutId="company-section-underline"
                                    className="absolute inset-x-1 -bottom-3 h-0.5 bg-[#F5F5F5]"
                                    transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE }}
                                />
                            )}
                        </button>
                    ))}
                </div>
            </nav>
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  Stat Callout — large display number with label                     */
/* ------------------------------------------------------------------ */

function StatCallout({
    stats,
    variant,
    shouldReduceMotion,
}: {
    stats: { value: string; label: string }[];
    variant: 'teal' | 'ivory';
    shouldReduceMotion: boolean | null;
}) {
    const colors = sectionColors(variant);

    const reveal = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-60px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    return (
        <div className={`mx-auto flex max-w-2xl flex-wrap items-start justify-center gap-10 py-6 sm:gap-16 lg:gap-20`}>
            {stats.map((stat, i) => (
                <motion.div key={stat.label} {...reveal(i * 0.1)} className="text-center">
                    <span className={`lux-didot block ${colors.heading}`} style={{ fontSize: 'clamp(3rem, 7vw, 5rem)', lineHeight: 1 }}>
                        {stat.value}
                    </span>
                    <span className={`lux-caps lux-caps-sm mt-3 block ${colors.muted}`}>{stat.label}</span>
                </motion.div>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Quote Interstitial — breathing moment between sections             */
/* ------------------------------------------------------------------ */

function QuoteInterstitial({
    quote,
    attribution,
    variant,
    shouldReduceMotion,
}: {
    quote: string;
    attribution: string;
    variant: 'teal' | 'ivory';
    shouldReduceMotion: boolean | null;
}) {
    const colors = sectionColors(variant);

    const reveal = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-80px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    return (
        <section className={`${colors.bg} py-20 sm:py-24 lg:py-28`}>
            <div className="nav-px">
                <blockquote className="mx-auto max-w-3xl text-center">
                    {/* Diamond ornament */}
                    <motion.svg {...reveal()} width="10" height="10" viewBox="0 0 10 10" className={`mx-auto mb-8 ${colors.accent}`}>
                        <rect x="5" y="0" width="7.07" height="7.07" rx="0.75" transform="rotate(45 5 0)" fill="currentColor" />
                    </motion.svg>

                    <motion.p
                        {...reveal(0.1)}
                        className={`lux-didot italic ${colors.heading}`}
                        style={{ fontSize: 'clamp(1.625rem, 4vw, 2.75rem)', lineHeight: 1.2 }}
                    >
                        &ldquo;{quote}&rdquo;
                    </motion.p>

                    <motion.footer {...reveal(0.2)} className={`lux-caps lux-caps-sm mt-8 ${colors.muted}`}>
                        {attribution}
                    </motion.footer>
                </blockquote>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/*  Decorative Divider — diamond ornament between sections             */
/* ------------------------------------------------------------------ */

function DecorativeDivider({ variant }: { variant: 'teal' | 'ivory' }) {
    const colors = sectionColors(variant);

    return (
        <div className={`${colors.bg} flex items-center justify-center`}>
            <div className="flex w-full max-w-xs items-center gap-4 px-8">
                <div className={`h-px flex-1 ${colors.rule}`} />
                <svg width="6" height="6" viewBox="0 0 6 6" className={colors.accent}>
                    <rect x="3" y="0" width="4.24" height="4.24" rx="0.5" transform="rotate(45 3 0)" fill="currentColor" />
                </svg>
                <div className={`h-px flex-1 ${colors.rule}`} />
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Section Header — kicker + heading + optional subheading            */
/* ------------------------------------------------------------------ */

function SectionHeader({
    kicker,
    heading,
    subheading,
    variant,
    shouldReduceMotion,
}: {
    kicker: string;
    heading: string;
    subheading?: string;
    variant: 'teal' | 'ivory';
    shouldReduceMotion: boolean | null;
}) {
    const reveal = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-60px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    const isTeal = variant === 'teal';

    return (
        <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-12 lg:mb-14">
            <motion.span {...reveal()} className={`lux-caps lux-caps-lg inline-block ${isTeal ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60'}`}>
                {kicker}
            </motion.span>
            <motion.h2 {...reveal(0.1)} className={`lux-didot lux-didot-lg mt-4 ${isTeal ? 'text-[#F5F5F5]' : 'text-[#0e2727]'}`}>
                {heading}
            </motion.h2>
            {subheading && (
                <motion.p
                    {...reveal(0.15)}
                    className={`lux-body lux-body-lg mx-auto mt-4 max-w-xl ${isTeal ? 'text-[#F5F5F5]/75' : 'text-[#0e2727]/75'}`}
                >
                    {subheading}
                </motion.p>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Press Card — elevated press citation                               */
/* ------------------------------------------------------------------ */

function PressCard({
    href,
    logo,
    logoAlt,
    logoNode,
    headline,
    source,
    variant,
    shouldReduceMotion,
    delay = 0,
}: {
    href: string;
    logo?: string;
    logoAlt?: string;
    logoNode?: React.ReactNode;
    headline: string;
    source: string;
    variant: 'teal' | 'ivory';
    shouldReduceMotion: boolean | null;
    delay?: number;
}) {
    const reveal = (d = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 16 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-60px' as const },
                  transition: { duration: 0.6, ease: EASE, delay: d },
              };

    const isTeal = variant === 'teal';

    return (
        <motion.a
            {...reveal(delay)}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`group block overflow-hidden rounded-sm border transition-colors duration-500 focus-visible:outline-none ${
                isTeal
                    ? 'border-[#F5F5F5]/10 bg-[#0b2222] hover:border-[#F5F5F5]/25 focus-visible:border-[#F5F5F5]/40'
                    : 'border-[#0e2727]/10 hover:border-[#0e2727]/25 focus-visible:border-[#0e2727]/40'
            }`}
        >
            <div className="flex items-center gap-3 px-4 py-4 sm:gap-5 sm:px-6 sm:py-5">
                {logo ? (
                    <img
                        src={logo}
                        alt={logoAlt || ''}
                        loading="lazy"
                        className={`h-5 w-auto flex-shrink-0 opacity-50 brightness-0 ${isTeal ? 'invert' : ''}`}
                    />
                ) : (
                    <span
                        aria-hidden="true"
                        className={`lux-caps lux-caps-sm flex-shrink-0 leading-tight ${isTeal ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60'}`}
                    >
                        {logoNode || logoAlt}
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <p
                        className={`lux-body lux-body-sm font-medium transition-colors duration-300 ${
                            isTeal ? 'text-[#F5F5F5]/80 group-hover:text-[#F5F5F5]' : 'text-[#0e2727]/80 group-hover:text-[#0e2727]'
                        }`}
                    >
                        {headline}
                    </p>
                    <p className={`lux-caps lux-caps-sm mt-1 ${isTeal ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60'}`}>{source}</p>
                </div>
                <span
                    className={`hidden flex-shrink-0 transition-colors duration-300 sm:inline ${
                        isTeal ? 'text-[#F5F5F5]/20 group-hover:text-[#F5F5F5]/60' : 'text-[#0e2727]/20 group-hover:text-[#0e2727]/60'
                    }`}
                >
                    &rarr;
                </span>
            </div>
        </motion.a>
    );
}

/* ------------------------------------------------------------------ */
/*  Closing CTA — "Choose Your Route"                                  */
/* ------------------------------------------------------------------ */

function ClosingCTA({ shouldReduceMotion }: { shouldReduceMotion: boolean | null }) {
    const [hovered, setHovered] = useState<'client' | 'agent' | null>(null);

    const reveal = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-80px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    return (
        <section className="nav-px bg-[#0e2727] py-20 sm:py-24 lg:py-32">
            <div className="mx-auto max-w-3xl">
                {/* Section header */}
                <div className="mb-12 text-center sm:mb-16 lg:mb-20">
                    <motion.span {...reveal()} className="lux-caps lux-caps-lg inline-block text-[#F5F5F5]/60">
                        Your Journey Continues
                    </motion.span>
                    <motion.h2 {...reveal(0.1)} className="lux-didot lux-didot-lg mt-4 text-[#F5F5F5]">
                        Choose your route.
                    </motion.h2>
                    <motion.p {...reveal(0.2)} className="lux-body lux-body-lg mx-auto mt-4 max-w-md text-[#F5F5F5]/75">
                        Two paths, one destination — becoming part of the LuxRally story.
                    </motion.p>
                </div>

                {/* Fork-in-the-road SVG */}
                <motion.div {...reveal(0.25)} className="mb-12 lg:mb-16">
                    <svg viewBox="0 0 400 140" className="mx-auto w-full max-w-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {/* Approaching road — dashed center line */}
                        <line x1="200" y1="140" x2="200" y2="82" stroke="#F5F5F5" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="6 4" />

                        {/* Left fork — client path */}
                        <path
                            d="M200 82 Q180 60 100 30"
                            stroke="#F5F5F5"
                            strokeWidth="1"
                            strokeDasharray="6 4"
                            className="transition-all duration-500"
                            strokeOpacity={hovered === 'client' ? 0.5 : 0.12}
                        />

                        {/* Right fork — agent path */}
                        <path
                            d="M200 82 Q220 60 300 30"
                            stroke="#F5F5F5"
                            strokeWidth="1"
                            strokeDasharray="6 4"
                            className="transition-all duration-500"
                            strokeOpacity={hovered === 'agent' ? 0.5 : 0.12}
                        />

                        {/* Fork diamond */}
                        <rect x="194" y="76" width="12" height="12" rx="1" transform="rotate(45 200 82)" fill="#F5F5F5" fillOpacity="0.6" />

                        {/* Tiny car silhouette at fork */}
                        <g opacity="0.35">
                            <rect x="193" y="92" width="14" height="6" rx="2" fill="#F5F5F5" />
                            <rect x="196" y="89" width="8" height="4" rx="1" fill="#F5F5F5" fillOpacity="0.7" />
                            <circle cx="196" cy="99" r="1.5" fill="#F5F5F5" fillOpacity="0.5" />
                            <circle cx="204" cy="99" r="1.5" fill="#F5F5F5" fillOpacity="0.5" />
                        </g>

                        {/* Left destination marker */}
                        <g className="transition-all duration-500" opacity={hovered === 'client' ? 0.8 : 0.25}>
                            <circle cx="100" cy="24" r="6" stroke="#F5F5F5" strokeWidth="1.5" fill="none" />
                            <circle cx="100" cy="24" r="2" fill="#F5F5F5" />
                            <line x1="100" y1="30" x2="100" y2="36" stroke="#F5F5F5" strokeWidth="1.5" />
                        </g>

                        {/* Right destination marker */}
                        <g className="transition-all duration-500" opacity={hovered === 'agent' ? 0.8 : 0.25}>
                            <circle cx="300" cy="24" r="6" stroke="#F5F5F5" strokeWidth="1.5" fill="none" />
                            <circle cx="300" cy="24" r="2" fill="#F5F5F5" />
                            <line x1="300" y1="30" x2="300" y2="36" stroke="#F5F5F5" strokeWidth="1.5" />
                        </g>
                    </svg>
                </motion.div>

                {/* Two-column card grid */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                    {/* Client card */}
                    <motion.div {...reveal(0.3)} className="flex">
                        <Link
                            href="/clients"
                            className="group/client relative flex flex-1 flex-col rounded-sm border border-[#F5F5F5]/10 p-7 transition-all duration-500 hover:border-[#F5F5F5]/40 hover:bg-[#F5F5F5]/[0.04] focus-visible:border-[#F5F5F5]/40 focus-visible:bg-[#F5F5F5]/[0.04] focus-visible:outline-none lg:p-10"
                            onMouseEnter={() => setHovered('client')}
                            onMouseLeave={() => setHovered(null)}
                        >
                            {/* Globe icon */}
                            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-[#F5F5F5]/15 transition-all duration-500 group-hover/client:border-[#F5F5F5]/40 group-hover/client:bg-[#F5F5F5]/15 group-focus-visible/client:border-[#F5F5F5]/40 group-focus-visible/client:bg-[#F5F5F5]/15">
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-[#F5F5F5]/60 transition-colors duration-500 group-hover/client:text-white group-focus-visible/client:text-white"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M2 12h20" />
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                </svg>
                            </div>

                            <h3 className="lux-didot lux-didot-md text-[#F5F5F5]">Travel with us</h3>
                            <p className="lux-body lux-body-sm mt-3 text-[#F5F5F5]/60">
                                Insider rates at the world&rsquo;s best hotels, booked in minutes. No lengthy discovery calls — just transparent
                                pricing and a team that moves at your speed.
                            </p>

                            <span className="lux-caps lux-caps-sm mt-auto inline-flex items-center gap-2 pt-6 text-[#F5F5F5]/60 transition-colors duration-300 group-hover/client:text-[#F5F5F5] group-focus-visible/client:text-[#F5F5F5]">
                                Become a Client
                                <span className="inline-block transition-transform duration-300 group-hover/client:translate-x-1 group-focus-visible/client:translate-x-1">
                                    &rarr;
                                </span>
                            </span>
                        </Link>
                    </motion.div>

                    {/* Agent card */}
                    <motion.div {...reveal(0.4)} className="flex">
                        <Link
                            href="/agents/become-an-agent/apply"
                            className="group/agent relative flex flex-1 flex-col rounded-sm border border-[#F5F5F5]/10 p-7 transition-all duration-500 hover:border-[#F5F5F5]/40 hover:bg-[#F5F5F5]/[0.04] focus-visible:border-[#F5F5F5]/40 focus-visible:bg-[#F5F5F5]/[0.04] focus-visible:outline-none lg:p-10"
                            onMouseEnter={() => setHovered('agent')}
                            onMouseLeave={() => setHovered(null)}
                        >
                            {/* Briefcase icon */}
                            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-[#F5F5F5]/15 transition-all duration-500 group-hover/agent:border-[#F5F5F5]/40 group-hover/agent:bg-[#F5F5F5]/15 group-focus-visible/agent:border-[#F5F5F5]/40 group-focus-visible/agent:bg-[#F5F5F5]/15">
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-[#F5F5F5]/60 transition-colors duration-500 group-hover/agent:text-white group-focus-visible/agent:text-white"
                                >
                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                </svg>
                            </div>

                            <h3 className="lux-didot lux-didot-md text-[#F5F5F5]">Build your business</h3>
                            <p className="lux-body lux-body-sm mt-3 text-[#F5F5F5]/60">
                                FastBook technology, elite training, and a brand that opens doors. Join hundreds of agents who left the old playbook
                                behind.
                            </p>

                            <span className="lux-caps lux-caps-sm mt-auto inline-flex items-center gap-2 pt-6 text-[#F5F5F5]/60 transition-colors duration-300 group-hover/agent:text-[#F5F5F5] group-focus-visible/agent:text-[#F5F5F5]">
                                Become an Agent
                                <span className="inline-block transition-transform duration-300 group-hover/agent:translate-x-1 group-focus-visible/agent:translate-x-1">
                                    &rarr;
                                </span>
                            </span>
                        </Link>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Company({ teamMembers }: CompanyProps) {
    const shouldReduceMotion = useReducedMotion();

    const inViewProps = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-50px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    const cardInViewProps = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 16 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true },
                  transition: { duration: 0.5, ease: EASE, delay },
              };

    const reveal = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-60px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    const leadership = teamMembers.filter((m) => m.department === 'Leadership');
    const rest = teamMembers.filter((m) => m.department !== 'Leadership');

    return (
        <PublicLayout
            headerConfig={{
                position: 'absolute',
                elevation: 'none',
                scrollBehavior: 'alwaysVisible',
                textColor: '#F5F5F5',
                background: 'transparent',
            }}
        >
            <MetaHead
                title="About Our Company"
                description="Learn about LuxRally Travel — from an exotic car rally to one of the fastest-growing luxury travel agencies in the world."
            />

            {/* ============================================================ */}
            {/*  Section 1 — Cinema Band Hero                               */}
            {/* ============================================================ */}
            <section className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden">
                {/* Video background with breathing animation */}
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster="https://cdnweb.luxrallytravel.com/images/rally-video-poster.jpg"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={shouldReduceMotion ? undefined : { animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                >
                    <source
                        src="https://cdnweb.luxrallytravel.com/parity-migration/2025/08/8f0f88f8-3f32-4a90-8908-a5c595bd42fb.mp4"
                        type="video/mp4"
                    />
                </video>

                {/* Full overlay scrim — even coverage for centered text */}
                <div className="absolute inset-0 z-[1] bg-black/50" />

                {/* Centered editorial content */}
                <motion.div
                    variants={containerVariants}
                    initial={shouldReduceMotion ? 'visible' : 'hidden'}
                    animate="visible"
                    className="relative z-10 mx-auto max-w-4xl px-6 text-center"
                >
                    <motion.span variants={itemVariants} className="lux-caps lux-caps-lg mb-6 inline-block text-white/60">
                        About LuxRally Travel
                    </motion.span>

                    <motion.h1
                        variants={itemVariants}
                        className="lux-didot text-white"
                        style={{ fontSize: 'clamp(3rem, 8vw, 7rem)', lineHeight: 0.95 }}
                    >
                        We started at
                        <br />
                        200&nbsp;mph.
                    </motion.h1>

                    <motion.p variants={itemVariants} className="lux-body lux-body-lg mx-auto mt-8 max-w-lg text-white/75">
                        What began as an exotic car rally became one of the fastest-growing travel agencies in the world.
                    </motion.p>
                </motion.div>

                {/* Scroll indicator — matches homepage SplitHero pattern */}
                <motion.div
                    initial={shouldReduceMotion ? {} : { opacity: 0 }}
                    animate={shouldReduceMotion ? {} : { opacity: 1 }}
                    transition={shouldReduceMotion ? {} : { duration: 0.8, delay: 1.2 }}
                    className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 md:bottom-12"
                >
                    <button
                        type="button"
                        onClick={() => {
                            const next = document.getElementById('origins');
                            if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="flex flex-col items-center gap-2 text-white/60 transition-opacity duration-300 hover:opacity-80"
                    >
                        <span className="lux-caps lux-caps-sm">Scroll</span>
                        <motion.svg
                            animate={shouldReduceMotion ? {} : { y: [0, 6, 0] }}
                            transition={shouldReduceMotion ? {} : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <path d="M10 4v12M5 11l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </motion.svg>
                    </button>
                </motion.div>
            </section>

            <SectionNav shouldReduceMotion={shouldReduceMotion} />

            {/* ============================================================ */}
            {/*  Section 2 — Alternating Timeline Sections                  */}
            {/* ============================================================ */}

            {/* ── Origins ──────────────────────────────────────────────── */}
            <section id="origins" className="nav-px scroll-mt-28 bg-[#0e2727] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader
                    kicker="Origins"
                    heading="From cult-favorite rally to disruptive travel&nbsp;agency."
                    variant="teal"
                    shouldReduceMotion={shouldReduceMotion}
                />

                <motion.div {...reveal(0.1)} className="mx-auto mb-10 max-w-2xl overflow-hidden rounded-sm lg:mb-12">
                    <div className="aspect-video">
                        <iframe
                            src="https://www.youtube.com/embed/ztzk7WqIGN8"
                            title="VinWiki — LuxRally Story"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            className="h-full w-full border-0"
                        />
                    </div>
                </motion.div>

                <motion.div {...reveal(0.15)} className="mx-auto max-w-xl text-center">
                    <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                        David Eisen created LuxRally &mdash; an exotic car rally that became a cult favorite in the car world. Known for Forbes
                        Five-Star hotels, epic routes, and Michelin-starred dining, it attracted disruptors comfortable at 200&nbsp;mph. Luxie, the
                        Eisens&rsquo; beagle, would join them on the rally &mdash; a furry fixture riding shotgun through every stop.
                    </p>
                </motion.div>

                <motion.div {...reveal(0.2)} className="mx-auto mt-8 max-w-xl text-center">
                    <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                        The rally was featured on car influencer channels like VinWiki, founded by Ed Bolian &mdash; an OG LuxRallier. Stories from
                        the road introduced the LuxRally lifestyle to everyone from car photographers to world-renowned car collectors.
                    </p>
                </motion.div>
            </section>

            {/* ── The Discovery ────────────────────────────────────────── */}
            <section id="discovery" className="nav-px scroll-mt-28 bg-[#fdfaf3] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader
                    kicker="The Discovery"
                    heading="A hotel deal changed everything."
                    variant="ivory"
                    shouldReduceMotion={shouldReduceMotion}
                />

                <motion.div {...reveal(0.1)} className="mx-auto max-w-xl text-center">
                    <p className="lux-body lux-body-lg text-[#0e2727]/75">
                        A hotel offered a group rate for the rally. David did some research and discovered that, as a travel agent, he could get
                        40&ndash;60% off hotels for his group reservations. He quickly secured his IATA credentials.
                    </p>
                </motion.div>

                <motion.div {...reveal(0.15)} className="mx-auto mt-8 max-w-xl text-center">
                    <p className="lux-body lux-body-lg text-[#0e2727]/75">
                        Word spread. Within a few years, David was earning millions in commissions. He didn&rsquo;t do things like other agents
                        &mdash; bookings in minutes, blunt feedback, no lengthy discovery calls. Clients couldn&rsquo;t get enough. He was the
                        antithesis of a traditional agent.
                    </p>
                </motion.div>
            </section>

            <QuoteInterstitial
                quote="I looked at how every other travel agent worked and did the exact opposite."
                attribution="David Eisen, Founder &amp; CEO"
                variant="teal"
                shouldReduceMotion={shouldReduceMotion}
            />

            {/* ── 2021 ─────────────────────────────────────────────────── */}
            <section id="2021" className="nav-px scroll-mt-28 bg-[#0e2727] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader kicker="2021" heading="Forbes takes notice." variant="teal" shouldReduceMotion={shouldReduceMotion} />

                <div className="mx-auto grid max-w-4xl grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                    {/* Left — press card */}
                    <div>
                        <PressCard
                            href="https://www.forbes.com/sites/zengernews/2021/08/26/evolution-of-the-travel-business-how-one-travel-agent-managed-to-score-big-during-pandemic/"
                            logo="/forbes.png.webp"
                            logoAlt="Forbes"
                            headline="Evolution of the Travel Business: How One Travel Agent Managed to Score Big During Pandemic"
                            source="Forbes · August 2021"
                            variant="teal"
                            shouldReduceMotion={shouldReduceMotion}
                            delay={0.15}
                        />
                    </div>

                    {/* Right — context */}
                    <motion.div {...reveal(0.1)}>
                        <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                            David&rsquo;s unconventional approach to luxury travel earns national press coverage. Forbes profiles how a car rally
                            organizer became one of the most successful travel agents in the industry.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── 2022 ─────────────────────────────────────────────────── */}
            <section id="2022" className="nav-px scroll-mt-28 bg-[#fdfaf3] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader kicker="2022" heading="Building towards a vision." variant="ivory" shouldReduceMotion={shouldReduceMotion} />

                <div className="mx-auto grid max-w-4xl grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                    {/* Left — narrative */}
                    <motion.div {...reveal(0.1)}>
                        <p className="lux-body lux-body-lg text-[#0e2727]/75">
                            Frustrated by outdated technology and practices at his host agency &mdash; and fundamental disagreements over how a modern
                            travel agent should operate &mdash; David left and launched LuxRally Travel as its own host agency, securing its own IATA
                            credentials and joining Travel Leaders Network as its consortium.
                        </p>
                        <p className="lux-body lux-body-lg mt-6 text-[#0e2727]/75">
                            Joined by his wife Courtney Eisen and a small band of agents who noticed his exponential rise, he was finally free to
                            build towards his vision. David began coding the technology that would reshape the industry.
                        </p>
                    </motion.div>

                    {/* Right — credential logos */}
                    <motion.div {...reveal(0.2)} className="flex flex-col items-center justify-center gap-8 lg:gap-10">
                        <img src="/iata.svg" alt="IATA" loading="lazy" decoding="async" className="h-12 w-auto opacity-40 brightness-0 lg:h-14" />
                        <div className="h-px w-16 bg-[#0e2727]/15" />
                        <img
                            src="/TLN-logo.png"
                            alt="Travel Leaders Network"
                            loading="lazy"
                            decoding="async"
                            className="h-12 w-auto opacity-40 brightness-0 lg:h-14"
                        />
                    </motion.div>
                </div>
            </section>

            {/* ── 2023 ─────────────────────────────────────────────────── */}
            <section id="2023" className="nav-px scroll-mt-28 bg-[#0e2727] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader kicker="2023" heading="600 applications per month." variant="teal" shouldReduceMotion={shouldReduceMotion} />

                <StatCallout
                    stats={[
                        { value: '600+', label: 'Monthly applications' },
                        { value: '~300', label: 'Agents onboarded' },
                    ]}
                    variant="teal"
                    shouldReduceMotion={shouldReduceMotion}
                />

                <motion.div {...reveal(0.15)} className="mx-auto max-w-xl pt-4 pb-8 text-center">
                    <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                        The training program launches, attracting hundreds of applicants from industries far beyond traditional travel.
                    </p>
                </motion.div>

                <div className="mx-auto max-w-lg">
                    <PressCard
                        href="https://www.forbes.com/sites/zengernews/2023/02/16/this-company-doesnt-hire-anyone-with-industry-experience--heres-why-its-business-is-thriving/"
                        logo="/forbes.png.webp"
                        logoAlt="Forbes"
                        headline="This Company Doesn't Hire Anyone With Industry Experience — Here's Why Its Business Is Thriving"
                        source="Forbes · February 2023"
                        variant="teal"
                        shouldReduceMotion={shouldReduceMotion}
                        delay={0.15}
                    />
                </div>
            </section>

            {/* ── 2024 ─────────────────────────────────────────────────── */}
            <section id="2024" className="nav-px scroll-mt-28 bg-[#fdfaf3] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader kicker="2024" heading="FastBook changes the game." variant="ivory" shouldReduceMotion={shouldReduceMotion} />

                <div className="mx-auto grid max-w-4xl grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
                    {/* Left — press cards */}
                    <div className="flex flex-col gap-3">
                        <PressCard
                            href="https://www.travelweekly.com/Travel-News/Travel-Agent-Issues/LuxRally-hotel-booking-tool"
                            logoNode={
                                <>
                                    Travel
                                    <br />
                                    Weekly
                                </>
                            }
                            headline="Host Agency LuxRally Turbocharges Its Hotel Booking Tool"
                            source="Travel Weekly · 2024"
                            variant="ivory"
                            shouldReduceMotion={shouldReduceMotion}
                            delay={0.15}
                        />
                        <PressCard
                            href="https://www.travelweekly.com/Travel-News/Travel-Agent-Issues/Insights/LuxRally-travel-turns-car-rally-to-host-travel-agency"
                            logoNode={
                                <>
                                    Travel
                                    <br />
                                    Weekly
                                </>
                            }
                            headline="A Passion for Exotic Cars Put This Agency in the Fast Lane"
                            source="Travel Weekly · 2024"
                            variant="ivory"
                            shouldReduceMotion={shouldReduceMotion}
                            delay={0.2}
                        />
                    </div>

                    {/* Right — context */}
                    <motion.div {...reveal(0.1)}>
                        <p className="lux-body lux-body-lg text-[#0e2727]/75">
                            FastBook technology and CRM launch. Additional talent joins to support rapid growth. Trade press covers the momentum.
                        </p>
                    </motion.div>
                </div>
            </section>

            <DecorativeDivider variant="teal" />

            {/* ── 2025 ─────────────────────────────────────────────────── */}
            <section id="2025" className="scroll-mt-28 bg-[#0e2727] md:scroll-mt-32">
                {/* Cinematic editorial frame */}
                <div className="nav-px py-20 sm:py-24 lg:py-32">
                    <SectionHeader kicker="2025" heading="A historic achievement." variant="teal" shouldReduceMotion={shouldReduceMotion} />

                    {/* Stat callout — the crown jewel number */}
                    <StatCallout stats={[{ value: '1 of 63', label: 'Agencies worldwide' }]} variant="teal" shouldReduceMotion={shouldReduceMotion} />

                    {/* Endorsement badge */}
                    <motion.div {...reveal(0.1)} className="mx-auto mt-6 max-w-xl text-center">
                        <img
                            src="/EndorsedByLogo-FTG-REV.png"
                            alt="Forbes Travel Guide Endorsed"
                            loading="lazy"
                            decoding="async"
                            className="mx-auto mb-8 h-20 w-auto opacity-70 lg:h-24"
                        />
                        <p className="lux-body lux-body-lg text-[#F5F5F5]/80">
                            LuxRally Travel earns a Forbes Travel Guide endorsement &mdash; joining an exclusive group of just 63 agencies worldwide
                            to receive the distinction.
                        </p>
                    </motion.div>

                    {/* Thin decorative rule */}
                    <div className="mx-auto my-12 h-px max-w-xs bg-[#F5F5F5]/15 lg:my-16" />

                    {/* Blockquote — elevated treatment */}
                    <motion.div {...reveal(0.15)} className="mx-auto max-w-2xl text-center">
                        <blockquote>
                            <p className="lux-didot text-[#F5F5F5]/85 italic" style={{ fontSize: 'clamp(1.375rem, 3vw, 2rem)', lineHeight: 1.3 }}>
                                &ldquo;This marks a momentous occasion in the travel industry. LuxRally Travel&rsquo;s outstanding service and
                                exceptional reputation have earned them this prestigious endorsement.&rdquo;
                            </p>
                            <footer className="lux-caps lux-caps-sm mt-6 text-[#F5F5F5]/60">
                                &mdash; Richard Lebowitz, Senior Vice President, Forbes Travel Guide
                            </footer>
                        </blockquote>
                    </motion.div>

                    {/* Thin decorative rule */}
                    <div className="mx-auto my-12 h-px max-w-xs bg-[#F5F5F5]/15 lg:my-16" />

                    {/* Additional context */}
                    <motion.div {...reveal(0.2)} className="mx-auto max-w-xl text-center">
                        <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                            Tours, car transfers, car rentals, insurance, and other travel components added to FastBook. Agent training relaunched
                            with over 600 lessons and 400 videos &mdash; a comprehensive program designed so anyone from any industry can maximize the
                            advantages of being a travel agent.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── 2026 ─────────────────────────────────────────────────── */}
            <section id="2026" className="nav-px scroll-mt-28 bg-[#fdfaf3] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <SectionHeader
                    kicker="2026"
                    heading="And we&rsquo;re just getting started."
                    variant="ivory"
                    shouldReduceMotion={shouldReduceMotion}
                />

                <motion.div {...reveal(0.1)} className="mx-auto max-w-xl text-center">
                    <p className="lux-body lux-body-lg text-[#0e2727]/75">
                        Full brand refresh. LuxRally Travel launches proprietary agent-facing booking AI &mdash; powered by the immense number of
                        rates and data points pulled by FastBook. Because it&rsquo;s built for agents, not consumers, it surfaces non-public rates
                        that wouldn&rsquo;t be available on any guest-facing platform.
                    </p>
                </motion.div>
            </section>

            {/* ============================================================ */}
            {/*  Section 3 — Team Grid (editorial-framed on teal)            */}
            {/* ============================================================ */}
            <section id="team" className="nav-px scroll-mt-28 bg-[#0e2727] py-20 sm:py-24 md:scroll-mt-32 lg:py-32">
                <div className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
                    <motion.span {...inViewProps()} className="lux-caps lux-caps-lg inline-block text-[#F5F5F5]/60">
                        Our Team
                    </motion.span>
                    <motion.h2 {...inViewProps(0.1)} className="lux-didot lux-didot-lg mt-4 text-[#F5F5F5]">
                        A team of disruptors.
                    </motion.h2>
                    <motion.p {...inViewProps(0.15)} className="lux-body lux-body-lg mx-auto mt-4 max-w-md text-[#F5F5F5]/75">
                        We have always been risk-takers with a sense of pushing the limits. This mindset is exactly what the travel agent industry
                        needed.
                    </motion.p>
                </div>

                {/* Leadership row — large editorial cards */}
                {leadership.length > 0 && (
                    <div
                        className={`grid gap-5 lg:gap-6 ${
                            leadership.length >= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'mx-auto grid-cols-1 sm:max-w-3xl sm:grid-cols-2'
                        }`}
                    >
                        {leadership.map((member, i) => (
                            <motion.div key={member.name} {...cardInViewProps(i * 0.08)}>
                                <Link
                                    href={`/team/${member.slug}`}
                                    className="group relative block overflow-hidden rounded-sm bg-[#0b2222] p-1.5 lg:p-2"
                                >
                                    <div className="relative aspect-[3/4] overflow-hidden rounded-[1px]">
                                        <img
                                            src={member.main_image}
                                            alt={member.name}
                                            loading="lazy"
                                            decoding="async"
                                            className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
                                        />
                                        <div
                                            className="absolute inset-x-0 bottom-0 px-4 pt-16 pb-4 sm:px-5 sm:pb-5"
                                            style={{
                                                background:
                                                    'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.15) 55%, transparent 70%)',
                                            }}
                                        >
                                            <p className="lux-body lux-body-sm font-medium text-white">{member.name}</p>
                                            <p className="lux-body lux-body-sm text-white/60">{member.position}</p>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Remaining team + CTA card — smaller editorial cards */}
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:mt-6 lg:grid-cols-4 lg:gap-4">
                    {rest.map((member, i) => (
                        <motion.div key={member.name} {...cardInViewProps(i * 0.05)}>
                            <Link href={`/team/${member.slug}`} className="group relative block overflow-hidden rounded-sm bg-[#0b2222] p-1 lg:p-1.5">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-[1px]">
                                    <img
                                        src={member.main_image}
                                        alt={member.name}
                                        loading="lazy"
                                        decoding="async"
                                        className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
                                    />
                                    <div
                                        className="absolute inset-x-0 bottom-0 px-3 pt-12 pb-3 sm:px-4 sm:pb-4"
                                        style={{
                                            background:
                                                'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 25%, rgba(0,0,0,0.1) 50%, transparent 65%)',
                                        }}
                                    >
                                        <p className="lux-body lux-body-sm font-medium text-white">{member.name}</p>
                                        <p className="lux-body lux-body-sm text-white/60">{member.position}</p>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}

                    {/* CTA card */}
                    <motion.div {...cardInViewProps(rest.length * 0.05)}>
                        <Link
                            href="/the-team"
                            className="group relative flex aspect-[3/4] flex-col items-center justify-center overflow-hidden rounded-sm border border-[#F5F5F5]/10 transition-colors duration-500 hover:border-[#F5F5F5]/25"
                        >
                            <span className="lux-didot text-[clamp(2rem,4vw,3rem)] text-[#F5F5F5]/15 transition-colors duration-500 group-hover:text-[#F5F5F5]/25">
                                &rarr;
                            </span>
                            <span className="lux-caps lux-caps-sm mt-4 text-[#F5F5F5]/60 transition-colors duration-500 group-hover:text-[#F5F5F5]">
                                Meet our agents
                            </span>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ============================================================ */}
            {/*  Section 4 — Closing CTA                                   */}
            {/* ============================================================ */}
            <ClosingCTA shouldReduceMotion={shouldReduceMotion} />
        </PublicLayout>
    );
}
